import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Stack, TextField } from '@mui/material'

import { LanguageMap } from '../../../../../types/LanguageMap'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import {
  FeatureEffectFormValues,
  FeatureEffectIdError,
  featureEffectStep,
  FeatureEffectValueError,
  getChoiceName,
  getFeatureEffectIdError,
  getFeatureEffectValueError,
  getFeatureName,
  isFeatureEffectPairTaken,
  toFeatureEffectFormValues,
  toFeatureEffectRow,
} from '../../helpers/featureEffectHelpers'
import { FeatureEffectRow, FeatureEffectsMap } from '../../types/Motivations'

type FeatureEffectEditDialogProps = {
  open: boolean
  row?: FeatureEffectRow
  featureEffects: FeatureEffectsMap
  languageMap?: LanguageMap
  isSaving?: boolean
  onClose: () => void
  onSave: (row: FeatureEffectRow) => void
}

const idErrorKeys: Record<FeatureEffectIdError, string> = {
  required: 'internal-service-management:motivations_id_required',
  invalid: 'internal-service-management:motivations_id_invalid',
}

const valueErrorKeys: Record<FeatureEffectValueError, string> = {
  required: 'internal-service-management:motivations_value_required',
  invalid: 'internal-service-management:motivations_value_invalid',
}

export const FeatureEffectEditDialog: FC<FeatureEffectEditDialogProps> = ({ open, row, featureEffects, languageMap, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState, getValues, watch } = useForm<FeatureEffectFormValues>({
    mode: 'onChange',
    defaultValues: toFeatureEffectFormValues(row),
  })

  useEffect(() => {
    if (open) reset(toFeatureEffectFormValues(row))
  }, [open, row, reset])

  const featureLegacyId = watch('featureLegacyId')
  const choiceLegacyId = watch('choiceLegacyId')
  const nameHint = (name: string | undefined, id: string, unknownKey: string) => (name ? name : id && !getFeatureEffectIdError(id) ? t(unknownKey) : undefined)
  const submit = handleSubmit((values) => onSave(toFeatureEffectRow(values, languageMap)))

  return (
    <InternalFormDialog
      open={open}
      title={row ? t('internal-service-management:motivations_edit_feature_effect') : t('internal-service-management:motivations_add_feature_effect')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={isSaving || !formState.isValid}
      maxWidth="md"
    >
      <Stack spacing={3} pt={1}>
        <Stack direction="row" spacing={2}>
          <Controller
            name="featureLegacyId"
            control={control}
            rules={{
              deps: ['choiceLegacyId'],
              validate: (id) => {
                const error = getFeatureEffectIdError(id)
                return error ? t(idErrorKeys[error]) : true
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label={t('internal-service-management:motivations_feature_id')}
                error={!!fieldState.error}
                helperText={
                  fieldState.error?.message ||
                  nameHint(getFeatureName(languageMap, featureLegacyId), featureLegacyId, 'internal-service-management:motivations_unknown_feature')
                }
              />
            )}
          />
          <Controller
            name="choiceLegacyId"
            control={control}
            rules={{
              validate: (id) => {
                const error = getFeatureEffectIdError(id)
                if (error) return t(idErrorKeys[error])
                return isFeatureEffectPairTaken(featureEffects, getValues('featureLegacyId'), id, row)
                  ? t('internal-service-management:motivations_pair_taken')
                  : true
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label={t('internal-service-management:motivations_choice_id')}
                error={!!fieldState.error}
                helperText={
                  fieldState.error?.message ||
                  nameHint(getChoiceName(languageMap, choiceLegacyId), choiceLegacyId, 'internal-service-management:motivations_unknown_choice')
                }
              />
            )}
          />
        </Stack>
        <Box display="grid" gridTemplateColumns={{ xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }} gap={2}>
          {toFeatureEffectFormValues().effects.map(({ type }, index) => (
            <Box key={type}>
              <Controller
                name={`effects.${index}.value`}
                control={control}
                rules={{
                  validate: (value) => {
                    const error = getFeatureEffectValueError(value)
                    return error ? t(valueErrorKeys[error]) : true
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    size="small"
                    label={type}
                    inputProps={{ step: featureEffectStep }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          ))}
        </Box>
      </Stack>
    </InternalFormDialog>
  )
}
